import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Beef, User, Mail, Phone, Calendar, ArrowLeft, Home } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function ProfilePage({ user: initialUser, onLogout }) {
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      setUser(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-[#5D1822] text-xl">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="profile-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link 
              to={user.role === 'admin' ? '/admin' : '/dashboard'} 
              className="flex items-center gap-2 text-[#5D1822] hover:text-[#3A0F15] transition-colors"
              data-testid="home-link"
            >
              <Home size={20} />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-[#E5E5E5]"></div>
            <div className="flex items-center gap-3">
              <Beef className="w-8 h-8 text-[#5D1822]" />
              <h1 className="text-2xl font-serif font-bold text-[#5D1822]">Macelleria Borgese</h1>
            </div>
          </div>
          <div></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <Link 
          to={user.role === 'admin' ? '/admin' : '/dashboard'} 
          className="inline-flex items-center gap-2 text-[#5D1822] hover:text-[#3A0F15] mb-6"
          data-testid="back-link"
        >
          <ArrowLeft size={20} />
          Indietro
        </Link>

        <div className="bg-white rounded-2xl card-shadow p-8">
          {/* Profile Header */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-[#5D1822] rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-12 h-12 text-[#C5A059]" />
            </div>
            <h2 className="text-3xl font-serif font-bold text-[#5D1822] mb-2" data-testid="profile-name">{user.full_name}</h2>
            <span className="inline-block px-4 py-1 bg-[#C5A059]/10 text-[#C5A059] rounded-full text-sm font-medium" data-testid="user-role">
              {user.role === 'admin' ? 'Amministratore' : 'Cliente'}
            </span>
          </div>

          {/* Profile Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-4 p-4 bg-[#F9F7F2] rounded-lg" data-testid="email-info">
              <Mail className="w-5 h-5 text-[#5D1822]" />
              <div>
                <p className="text-sm text-[#5A5A5A]">{user.no_email_account ? 'Tipo di accesso' : 'Email'}</p>
                <p className="font-medium text-[#2C2C2C]">
                  {user.no_email_account ? 'Nome, telefono e password' : user.email}
                </p>
              </div>
            </div>

            {user.phone && (
              <div className="flex items-center gap-4 p-4 bg-[#F9F7F2] rounded-lg" data-testid="phone-info">
                <Phone className="w-5 h-5 text-[#5D1822]" />
                <div>
                  <p className="text-sm text-[#5A5A5A]">Telefono</p>
                  <p className="font-medium text-[#2C2C2C]">{user.phone}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 p-4 bg-[#F9F7F2] rounded-lg" data-testid="created-info">
              <Calendar className="w-5 h-5 text-[#5D1822]" />
              <div>
                <p className="text-sm text-[#5A5A5A]">Membro dal</p>
                <p className="font-medium text-[#2C2C2C]">
                  {new Date(user.created_at).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={onLogout}
            className="btn-secondary w-full mt-8"
            data-testid="logout-btn"
          >
            Esci
          </button>
        </div>
      </main>
    </div>
  );
}